import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { allocationVlans } from "../../api/endpoints/allocationVlans";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";

export function VlanGeneratePanel({
  siteSlug, canWrite, gateOk, onGenerated,
}: {
  siteSlug: string; canWrite: boolean; gateOk: boolean; onGenerated: () => void;
}) {
  const qc = useQueryClient();
  const template = useQuery({
    queryKey: qk.vlanTemplate(),
    queryFn: () => allocationVlans.vlanTemplate().then((r) => r.data),
  });
  // default selection = the non-optional tags
  const [selected, setSelected] = useState<Set<number> | null>(null);
  const tags = useMemo(() => {
    if (!template.data) return new Set<number>();
    if (selected) return selected;
    return new Set(template.data.filter((t) => !t.optional).map((t) => t.vlan_tag));
  }, [template.data, selected]);

  const generate = useMutation({
    mutationFn: () =>
      allocationVlans.generate(siteSlug, { vlan_tags: [...tags].sort((a, b) => a - b) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.allocationLedger() });
      onGenerated();
    },
  });

  function toggle(tag: number) {
    const next = new Set(tags);
    if (next.has(tag)) next.delete(tag);
    else next.add(tag);
    setSelected(next);
  }

  return (
    <div style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}>
      <div className="label">Generate VLANs · {siteSlug}</div>
      {template.isPending && <p style={{ color: "var(--text-dim)" }}>Loading template…</p>}
      {template.data?.map((t) => (
        <label key={t.vlan_tag} style={{ display: "block", margin: "4px 0" }}>
          <input type="checkbox" checked={tags.has(t.vlan_tag)} onChange={() => toggle(t.vlan_tag)} />{" "}
          {t.vlan_tag} · {t.name} (/{t.prefix_len}){t.optional ? " · optional" : ""}
        </label>
      ))}
      <button
        type="button"
        disabled={!canWrite || !gateOk || tags.size === 0 || generate.isPending}
        onClick={() => generate.mutate()}
      >
        {generate.isPending ? "Generating…" : `Generate ${tags.size} VLAN(s)`}
      </button>
      {generate.error && <ErrorBanner error={generate.error} title="VLAN generation failed" />}
    </div>
  );
}
